// @ts-nocheck
/**
 * Standard template - traditional resume format with enhanced formatting
 * @param {Object} resumeData - Resume data object
 * @returns {string} HTML formatted resume
 */

/**
 * Helper function to escape HTML special characters to prevent malformed tags
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str || typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Helper function to safely create HTML with proper escaping
 * @param {string} tag - HTML tag name
 * @param {string} content - Content to place inside the tag
 * @param {Object} attributes - HTML attributes to add to the tag
 * @returns {string} HTML string
 */
function safeTag(tag, content, attributes = {}) {
  const attrs = Object.entries(attributes)
    .map(([key, value]) => `${key}="${escapeHtml(value)}"`)
    .join(" ");
  return `<${tag}${attrs ? " " + attrs : ""}>${content}</${tag}>`;
}

/**
 * This helper function logs all <strong> tags from the final HTML and prints the
 * fully parsed HTML structure. It's called right after we build up html.
 *
 * If <strong> is missing or malformed (due to single quotes, etc.), you'll see it
 * in the console logs.
 */
function debugFinalHTML(htmlString) {
  // Create a hidden <div> so the browser can parse the string exactly how it would in the DOM
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  // 1) Log any <strong> tags we find
  const strongTags = tempDiv.querySelectorAll("strong");
  if (strongTags.length === 0) {
    console.warn(
      "[debugFinalHTML] No <strong> tags detected. (Possible malformed HTML?)"
    );
  } else {
    console.log(`[debugFinalHTML] Found ${strongTags.length} <strong> tags:`);
    strongTags.forEach((st, index) => {
      console.log(
        `[debugFinalHTML] <strong> #${index + 1} => "${st.textContent}"`
      );
    });
  }

  // 2) Check specifically for project sections with Skills Utilized and Outcome
  const projectSkills = tempDiv.querySelectorAll(
    "[data-element='project-skills']"
  );
  const projectOutcomes = tempDiv.querySelectorAll(
    "[data-element='project-outcome']"
  );

  console.log(
    `[debugFinalHTML] Found ${projectSkills.length} project-skills elements`
  );
  projectSkills.forEach((el, i) => {
    console.log(
      `[debugFinalHTML] project-skills #${i + 1} HTML: "${el.outerHTML}"`
    );
    const strongInSkills = el.querySelector("strong");
    if (strongInSkills) {
      console.log(
        `[debugFinalHTML] - Contains <strong>: "${strongInSkills.textContent}"`
      );
    } else {
      console.warn(
        "[debugFinalHTML] - Missing <strong> tag in project-skills!"
      );
    }
  });

  console.log(
    `[debugFinalHTML] Found ${projectOutcomes.length} project-outcome elements`
  );
  projectOutcomes.forEach((el, i) => {
    console.log(
      `[debugFinalHTML] project-outcome #${i + 1} HTML: "${el.outerHTML}"`
    );
    const strongInOutcome = el.querySelector("strong");
    if (strongInOutcome) {
      console.log(
        `[debugFinalHTML] - Contains <strong>: "${strongInOutcome.textContent}"`
      );
    } else {
      console.warn(
        "[debugFinalHTML] - Missing <strong> tag in project-outcome!"
      );
    }
  });

  // 3) Print out the final, fully parsed HTML structure
  console.log("[debugFinalHTML] Final parsed HTML is:\n", tempDiv.innerHTML);

  // 4) Return the HTML string for further processing if needed
  return htmlString;
}

export const standardTemplateHtml = (resumeData) => {
  if (!resumeData) return "";

  const {
    personalInformation,
    education,
    workExperience,
    additionalQualifications,
    targetJobInformation,
    skills,
  } = resumeData;

  // 1) Log the entire resumeData
  console.log("[standardTemplateHtml] Called with resumeData:", resumeData);
  console.log(
    "[standardTemplateHtml] additionalQualifications:",
    additionalQualifications
  );

  // Start with a container that identifies the template
  let html = `<div data-template="standard" class="resume-template">`;

  // --------------------
  // HEADER / PERSONAL INFO
  // --------------------
  if (personalInformation) {
    // Name in large font with data attribute for docx conversion
    html += `<h1 data-section="personal-info" data-element="name" style="font-family:'Times New Roman'; text-align: center;">${
      personalInformation.firstName || ""
    } ${personalInformation.lastName || ""}</h1>`;

    // Contact information and social links - centered, but more organized
    html += '<div style="text-align: center;" data-section="contact-info">';
    const contactInfo = [];
    const socialLinks = [];

    if (personalInformation.email)
      contactInfo.push(`${personalInformation.email}`);
    if (personalInformation.phone)
      contactInfo.push(`${personalInformation.phone}`);
    if (
      personalInformation.address &&
      !personalInformation.email?.includes(personalInformation.address) &&
      !personalInformation.phone?.includes(personalInformation.address)
    ) {
      contactInfo.push(`${personalInformation.address}`);
    }
    if (personalInformation.linkedinUrl) {
      const cleanUrl = personalInformation.linkedinUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      socialLinks.push(cleanUrl);
    }
    if (personalInformation.githubUrl) {
      const cleanUrl = personalInformation.githubUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      socialLinks.push(cleanUrl);
    }
    if (personalInformation.portfolioUrl) {
      const cleanUrl = personalInformation.portfolioUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      socialLinks.push(cleanUrl);
    }
    if (personalInformation.twitter) {
      if (
        personalInformation.twitter.includes("twitter.com") ||
        personalInformation.twitter.includes("x.com")
      ) {
        const cleanUrl = personalInformation.twitter
          .replace(/https?:\/\/(www\.)?/, "")
          .replace(/\/$/, "");
        socialLinks.push(cleanUrl);
      } else {
        const handle = personalInformation.twitter.startsWith("@")
          ? personalInformation.twitter
          : `@${personalInformation.twitter}`;
        socialLinks.push(handle);
      }
    }

    if (contactInfo.length > 0) {
      // Added inline margin-bottom
      html += `<div style="text-align: center; margin-bottom: 4px;" class="contact-details" data-element="contact-details">${contactInfo.join(
        " | "
      )}</div>`;
    }
    if (socialLinks.length > 0) {
      // Added inline margin-bottom
      html += `<div style="text-align: center; margin-bottom: 4px;" class="social-links" data-element="social-links">${socialLinks.join(
        " | "
      )}</div>`;
    }
    html += "</div>";
  }

  // ----------------------------------
  // PROFESSIONAL SUMMARY / OBJECTIVE
  // ----------------------------------
  const summaryText =
    personalInformation?.professionalSummary ||
    targetJobInformation?.professionalSummary ||
    "";
  if (summaryText) {
    html += `<div data-section="summary">
      <h2 data-section-header="true" data-element="section-header" style="font-family:'Times New Roman';">Professional Summary</h2>
      <p data-element="summary-content">${escapeHtml(summaryText)}</p>
    </div>`;
  }

  // -------------
  // SKILLS
  // -------------
  if (skills && skills.length > 0) {
    html += `<div data-section="skills">
      <h2 data-section-header="true" data-element="section-header" style="font-family:'Times New Roman';">Skills</h2>`;
    const categorizedSkills = {};
    let isSkillsCategorized = false;
    if (Array.isArray(skills)) {
      skills.forEach((skill) => {
        if (typeof skill === "object" && skill.category && skill.name) {
          isSkillsCategorized = true;
          if (!categorizedSkills[skill.category]) {
            categorizedSkills[skill.category] = [];
          }
          categorizedSkills[skill.category].push(skill.name);
        }
      });
      if (isSkillsCategorized) {
        Object.keys(categorizedSkills).forEach((category) => {
          // Added inline margin-bottom
          html += `<p style="margin-bottom: 4px;" class="skill-category" data-element="skill-category"><strong>${escapeHtml(
            category
          )}</strong>: ${escapeHtml(
            categorizedSkills[category].join(", ")
          )}</p>`;
        });
      } else {
        const skillList = skills
          .map((skill) =>
            typeof skill === "string" ? skill : skill.name || skill
          )
          .join(", ");
        // Added inline margin-bottom
        html += `<p style="margin-bottom: 4px;" class="skills-list" data-element="skills-list">${escapeHtml(
          skillList
        )}</p>`;
      }
    } else {
      html += "<p data-element='no-skills'>No skills listed</p>";
    }
    html += `</div>`;
  }

  // ------------------
  // WORK EXPERIENCE
  // ------------------
  if (workExperience && workExperience.length > 0) {
    html += `<div data-section="experience">
      <h2 data-section-header="true" data-element="section-header" style="font-family:'Times New Roman';">Work Experience</h2>`;
    workExperience.forEach((job) => {
      html += `<div data-element="job">`;
      html += `<h3 data-element="job-title" style="font-family:Times New Roman;">${escapeHtml(
        job.jobTitle || ""
      )} | ${escapeHtml(job.companyName || job.company || "")}</h3>`;
      const startDate = formatDate(job.startDate);
      const endDate =
        !job.endDate || job.endDate === "9999-01-01"
          ? "Present"
          : formatDate(job.endDate);
      html += `<p data-element="job-period">${escapeHtml(
        startDate
      )} - ${escapeHtml(endDate)} | ${escapeHtml(job.location || "")}</p>`;
      if (job.description) {
        html += `<p data-element="job-description">${escapeHtml(
          job.description
        )}</p>`;
      }
      if (job.responsibilities) {
        html += `<ul data-element="job-responsibilities">`;
        if (typeof job.responsibilities === "string") {
          const responsibilitiesList = job.responsibilities
            .split(/[\n\r]+|(?<=\.)(?=\s)/)
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
          responsibilitiesList.forEach((resp) => {
            html += `<li>${escapeHtml(resp)}</li>`;
          });
        } else if (Array.isArray(job.responsibilities)) {
          job.responsibilities.forEach((resp) => {
            if (typeof resp === "string") {
              html += `<li>${escapeHtml(resp)}</li>`;
            } else if (resp && typeof resp === "object") {
              html += `<li>${escapeHtml(
                resp.text || JSON.stringify(resp)
              )}</li>`;
            }
          });
        }
        html += `</ul>`;
      }
      if (job.achievements && job.achievements.length > 0) {
        html += `<p data-element="achievements-header"><strong>Achievements:</strong></p><ul data-element="job-achievements">`;
        job.achievements.forEach((achievement) => {
          html += `<li>${escapeHtml(achievement)}</li>`;
        });
        html += `</ul>`;
      }
      if (job.technologies && job.technologies.length > 0) {
        html += `<p data-element="job-technologies"><strong>Technologies Used:</strong> ${escapeHtml(
          job.technologies.join(", ")
        )}</p>`;
      }
      html += `</div>`;
    });
    html += `</div>`;
  }

  // -----------
  // EDUCATION
  // -----------
  if (education && education.length > 0) {
    html += `<div data-section="education">
      <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Education</h2>`;
    education.forEach((edu) => {
      const degree = edu.degree || "";
      const fieldOfStudy = edu.fieldOfStudy ? `in ${edu.fieldOfStudy}` : "";
      html += `<div data-element="education-item">`;
      // Removed inline style="font-family:Times New Roman;"
      html += `<h3 data-element="education-degree">${escapeHtml(
        degree
      )} ${escapeHtml(fieldOfStudy)}</h3>`; // Removed newline character again
      const graduationDate = edu.graduationDate
        ? formatDate(edu.graduationDate)
        : "";
      html += `<p data-element="education-institution">${escapeHtml(
        edu.institution || ""
      )} | ${escapeHtml(graduationDate)}</p>`;
      if (edu.gpa) {
        html += `<p data-element="education-gpa"><strong>GPA:</strong> ${escapeHtml(
          edu.gpa
        )}</p>`;
      }
      if (edu.description) {
        html += `<p data-element="education-description">${escapeHtml(
          edu.description
        )}</p>`;
      }
      if (edu.courses && edu.courses.length > 0) {
        html += `<p data-element="education-courses"><strong>Relevant Coursework:</strong> ${escapeHtml(
          edu.courses.join(", ")
        )}</p>`;
      }
      if (edu.honors && edu.honors.length > 0) {
        html += `<p data-element="education-honors-header"><strong>Honors & Awards:</strong></p><ul data-element="education-honors">`;
        edu.honors.forEach((honor) => {
          html += `<li>${escapeHtml(honor)}</li>`;
        });
        html += `</ul>`;
      }
      html += `</div>`;
    });
    html += `</div>`;
  }

  // -----------------------------------
  // ADDITIONAL QUALIFICATIONS (incl. Personal Projects)
  // -----------------------------------
  if (additionalQualifications) {
    html += `<div data-section="additional-qualifications">`;

    // If additionalQualifications is an object with .skills
    if (
      !Array.isArray(additionalQualifications) &&
      additionalQualifications.skills &&
      additionalQualifications.skills.length > 0
    ) {
      html += `<div data-element="additional-skills">
        <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Additional Skills</h2>`;
      if (additionalQualifications.skillsSummary) {
        html += `<p data-element="skills-summary">${escapeHtml(
          additionalQualifications.skillsSummary
        )}</p>`;
      }
      if (additionalQualifications.skillsWithDescriptions) {
        html += `<div data-element="skills-with-descriptions">`;
        additionalQualifications.skillsWithDescriptions.forEach((skill) => {
          html += `<p data-element="skill-with-description"><strong>${escapeHtml(
            skill.name
          )}</strong>: ${escapeHtml(skill.description)}</p>`;
        });
        html += `</div>`;
      } else {
        const skillList = additionalQualifications.skills
          .map((s) => escapeHtml(s))
          .join(", ");
        html += `<p data-element="skills-list">${skillList}</p>`;
      }
      html += `</div>`;
    }

    // If additionalQualifications is an array
    if (Array.isArray(additionalQualifications)) {
      console.log(
        "[standardTemplateHtml] Detected an ARRAY for additionalQualifications."
      );
      const qualificationsByType = {};
      additionalQualifications.forEach((qual) => {
        console.log("[standardTemplateHtml] qualifications item:", qual);
        if (!qualificationsByType[qual.qualificationType]) {
          qualificationsByType[qual.qualificationType] = [];
        }
        qualificationsByType[qual.qualificationType].push(qual.details);
      });

      // Work Authorization
      if (qualificationsByType["Work Authorization"]) {
        html += `<div data-section="work-authorization">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Work Authorization</h2>
          <ul data-element="authorization-list">`;
        qualificationsByType["Work Authorization"].forEach((auth) => {
          html += `<li data-element="authorization-item"><strong>Status</strong>: ${
            auth.status || "Not specified"
          }</li>`;
        });
        html += `</ul></div>`;
      }

      // Security Clearance
      if (qualificationsByType["Security Clearance"]) {
        html += `<div data-section="security-clearance">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Security Clearance</h2>
          <ul data-element="clearance-list">`;
        qualificationsByType["Security Clearance"].forEach((clearance) => {
          html += `<li data-element="clearance-item"><strong>Level</strong>: ${
            clearance.level || "Not specified"
          }</li>`;
        });
        html += `</ul></div>`;
      }

      // Personal Projects
      if (qualificationsByType["Personal Project"]) {
        console.log(
          "[standardTemplateHtml] Found 'Personal Project':",
          qualificationsByType["Personal Project"]
        );
        html += `<div data-section="projects">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Personal Projects</h2>`;
        qualificationsByType["Personal Project"].forEach((project) => {
          console.log(
            "[standardTemplateHtml] personal project detail:",
            project
          );
          html += `<div data-element="project">`;
          html += `<h3 data-element="project-name" style="font-family:Times New Roman;">${escapeHtml(
            project.projectName
          )}</h3>`;
          if (project.description) {
            html += `<p data-element="project-description">${escapeHtml(
              project.description
            )}</p>`;
          }
          if (project.skills) {
            const skillsText = Array.isArray(project.skills)
              ? project.skills.join(", ")
              : project.skills;
            const skillsLabel = `<strong class="preserve-bold">Skills Utilized:</strong>`;
            html += `<p data-element="project-skills">${skillsLabel} ${escapeHtml(
              skillsText
            )}</p>`;
          }
          if (project.outcome) {
            const outcomeLabel = `<strong class="preserve-bold">Outcome:</strong>`;
            html += `<p data-element="project-outcome">${outcomeLabel} ${escapeHtml(
              project.outcome
            )}</p>`;
          }
          if (project.projectUrl) {
            html += `<p data-element="project-url"><strong>Project URL:</strong> <a href="${escapeHtml(
              project.projectUrl
            )}">${escapeHtml(project.projectUrl)}</a></p>`;
          }
          html += `</div>`;
        });
        html += `</div>`;
      }

      // Certification
      if (qualificationsByType["Certification"]) {
        html += `<div data-section="certifications">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Certifications</h2>
          <ul data-element="certification-list">`;
        qualificationsByType["Certification"].forEach((cert) => {
          html += `<li data-element="certification-item"><strong>${escapeHtml(
            cert.name || "Certification"
          )}</strong>`;
          if (cert.issuer) {
            html += ` (Issuer: <strong>${escapeHtml(cert.issuer)}</strong>`;
            if (cert.dateObtained) {
              html += `, <strong>${escapeHtml(
                formatDate(cert.dateObtained)
              )}</strong>`;
            }
            html += `)`;
          }
          if (cert.description) {
            html += `<br>${escapeHtml(cert.description)}`;
          }
          html += `</li>`;
        });
        html += `</ul></div>`;
      }

      // Patent
      if (qualificationsByType["Patent"]) {
        html += `<div data-section="patents">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Patents</h2>
          <ul data-element="patent-list">`;
        qualificationsByType["Patent"].forEach((patent) => {
          html += `<li data-element="patent-item"><strong>${escapeHtml(
            patent.patentTitle
          )}</strong> (Patent #${escapeHtml(patent.patentNumber)})`;
          if (patent.dateGranted) {
            html += ` - Granted: ${escapeHtml(formatDate(patent.dateGranted))}`;
          }
          if (patent.description) {
            html += `<br>${escapeHtml(patent.description)}`;
          }
          html += `</li>`;
        });
        html += `</ul></div>`;
      }

      // Publication
      if (qualificationsByType["Publication"]) {
        html += `<div data-section="publications">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Publications</h2>
          <ul data-element="publication-list">`;
        qualificationsByType["Publication"].forEach((pub) => {
          html += `<li data-element="publication-item"><strong>${escapeHtml(
            pub.title
          )}</strong>`;
          if (pub.publisher) {
            html += `, <strong>Publisher:</strong> ${escapeHtml(
              pub.publisher
            )}`;
          }
          if (pub.date) {
            html += ` (<strong>${escapeHtml(formatDate(pub.date))}</strong>)`;
          }
          if (pub.authors) {
            html += `<br><strong>Authors</strong>: ${escapeHtml(pub.authors)}`;
          }
          if (pub.description) {
            html += `<br>${escapeHtml(pub.description)}`;
          }
          if (pub.url) {
            html += `<br><a href="${escapeHtml(pub.url)}">Read Publication</a>`;
          }
          html += `</li>`;
        });
        html += `</ul></div>`;
      }

      // Research Experience
      if (qualificationsByType["Research Experience"]) {
        html += `<div data-section="research">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Research Experience</h2>`;
        qualificationsByType["Research Experience"].forEach((research) => {
          html += `<div data-element="research-item">
            <h3 data-element="research-title" style="font-family:Times New Roman;">${escapeHtml(
              research.projectTitle
            )}</h3>`;
          if (research.role) {
            html += `<p data-element="research-role"><strong>Role</strong>: ${escapeHtml(
              research.role
            )}</p>`;
          }
          if (research.institution) {
            html += `<p data-element="research-institution"><strong>Institution</strong>: ${escapeHtml(
              research.institution
            )}</p>`;
          }
          if (research.startDate && research.endDate) {
            html += `<p data-element="research-duration"><strong>Duration</strong>: ${escapeHtml(
              formatDate(research.startDate)
            )} - ${escapeHtml(formatDate(research.endDate))}</p>`;
          } else if (research.startDate) {
            html += `<p data-element="research-started"><strong>Started</strong>: ${escapeHtml(
              formatDate(research.startDate)
            )}</p>`;
          }
          if (research.description) {
            html += `<p data-element="research-description">${escapeHtml(
              research.description
            )}</p>`;
          }
          if (research.outcomes) {
            html += `<p data-element="research-outcomes"><strong>Outcomes</strong>: ${escapeHtml(
              research.outcomes
            )}</p>`;
          }
          html += `</div>`;
        });
        html += `</div>`;
      }

      // Volunteering
      if (qualificationsByType["Volunteering"]) {
        html += `<div data-section="volunteering">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Volunteer Experience</h2>`;
        qualificationsByType["Volunteering"].forEach((volunteer) => {
          html += `<div data-element="volunteer-item">
            <h3 data-element="volunteer-organization" style="font-family:Times New Roman;">${escapeHtml(
              volunteer.organization
            )}</h3>`;
          if (volunteer.role) {
            html += `<p data-element="volunteer-role"><strong>Role</strong>: ${escapeHtml(
              volunteer.role
            )}</p>`;
          }
          if (volunteer.startDate && volunteer.endDate) {
            html += `<p data-element="volunteer-duration"><strong>Duration</strong>: ${escapeHtml(
              formatDate(volunteer.startDate)
            )} - ${escapeHtml(formatDate(volunteer.endDate))}</p>`;
          } else if (volunteer.startDate) {
            html += `<p data-element="volunteer-started"><strong>Started</strong>: ${escapeHtml(
              formatDate(volunteer.startDate)
            )}</p>`;
          }
          if (volunteer.description) {
            html += `<p data-element="volunteer-description">${escapeHtml(
              volunteer.description
            )}</p>`;
          }
          html += `</div>`;
        });
        html += `</div>`;
      }

      // Professional Development
      if (qualificationsByType["Professional Development"]) {
        html += `<div data-section="professional-development">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Professional Development</h2>
          <ul data-element="development-list">`;
        qualificationsByType["Professional Development"].forEach((dev) => {
          html += `<li data-element="development-item"><strong>${escapeHtml(
            dev.name
          )}</strong>`;
          if (dev.type) {
            html += ` (${escapeHtml(dev.type)})`;
          }
          if (dev.date) {
            html += ` - ${escapeHtml(formatDate(dev.date))}`;
          }
          if (dev.organizer) {
            html += `<br><strong>Organizer</strong>: ${escapeHtml(
              dev.organizer
            )}`;
          }
          if (dev.location) {
            html += `<br><strong>Location</strong>: ${escapeHtml(
              dev.location
            )}`;
          }
          if (dev.description) {
            html += `<br>${escapeHtml(dev.description)}`;
          }
          html += `</li>`;
        });
        html += `</ul></div>`;
      }

      // Awards
      if (qualificationsByType["Award"]) {
        html += `<div data-section="awards">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Awards & Honors</h2>
          <ul data-element="awards-list">`;
        qualificationsByType["Award"].forEach((award) => {
          html += `<li data-element="award-item"><strong>${escapeHtml(
            award.title
          )}</strong>`;
          if (award.issuer)
            html += ` from <strong>${escapeHtml(award.issuer)}</strong>`;
          if (award.date)
            html += ` (<strong>${escapeHtml(formatDate(award.date))}</strong>)`;
          if (award.description) html += `<br>${escapeHtml(award.description)}`;
          html += `</li>`;
        });
        html += `</ul></div>`;
      }

      // Languages
      if (qualificationsByType["Language"]) {
        html += `<div data-section="languages">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Languages</h2>`;
        qualificationsByType["Language"].forEach((lang) => {
          html += `<p data-element="language-item"><strong>${escapeHtml(
            lang.name
          )}</strong>: ${escapeHtml(lang.proficiency || "Fluent")}</p>`;
        });
        html += `</div>`;
      } else if (
        additionalQualifications.languages &&
        additionalQualifications.languages.length > 0
      ) {
        html += `<div data-section="languages">
          <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">Languages</h2>`;
        if (additionalQualifications.languagesWithProficiency) {
          additionalQualifications.languagesWithProficiency.forEach((lang) => {
            html += `<p data-element="language-item"><strong>${escapeHtml(
              lang.name
            )}</strong>: ${escapeHtml(lang.proficiency)}</p>`;
          });
        } else {
          const languageList = additionalQualifications.languages
            .map((l) => escapeHtml(l))
            .join(", ");
          html += `<p data-element="language-list">${languageList}</p>`;
        }
        html += `</div>`;
      }
    }
    html += `</div>`;
  }

  // ------------
  // REFERENCES
  // ------------
  if (resumeData.references && resumeData.references.length > 0) {
    html += `<div data-section="references">
      <h2 data-section-header="true" data-element="section-header" style="font-family:Times New Roman;">References</h2>`;
    if (resumeData.references[0].available === false) {
      html += `<p data-element="references-available"><em>References available upon request</em></p>`;
    } else {
      html += `<ul data-element="references-list">`;
      resumeData.references.forEach((ref) => {
        html += `<li data-element="reference-item"><strong>${escapeHtml(
          ref.name
        )}</strong>`;
        if (ref.title) html += `, ${escapeHtml(ref.title)}`;
        if (ref.company) html += `, ${escapeHtml(ref.company)}`;
        html += `<br>`;
        if (ref.email) html += `${escapeHtml(ref.email)}`;
        if (ref.phone) html += ` | ${escapeHtml(ref.phone)}`;
        html += `</li>`;
      });
      html += `</ul>`;
    }
    html += `</div>`;
  }

  html += `</div>`;

  // Final debug: confirm <strong> presence
  const finalHtml = debugFinalHTML(html);
  console.log(
    "[standardTemplateHtml] FINAL HTML OUTPUT (for cloud function):",
    finalHtml
  );

  return finalHtml;
};

/**
 * Helper function to format dates consistently
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date string
 */
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    const options = { month: "long", year: "numeric" };
    return date.toLocaleDateString(undefined, options);
  } catch (error) {
    return dateString;
  }
};

export default standardTemplateHtml;
